import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";

/**
 * A React component that renders a user's signature on a canvas from a JSON-encoded string of paths.
 *
 * Example of `signaturePaths`:
 * ```json
 * [
 *   [
 *     {"x":113,"y":78.75,"time":1732529443495,"color":"black"},
 *     {"x":109,"y":87.75,"time":1732529443562,"color":"black"}
 *   ]
 * ]
 * ```
 *
 * @param {Object} props - The component props.
 * @param {string} props.signaturePaths - A JSON string representing the signature paths. Each path contains
 * `x`, `y`, `time`, and optionally `color`.
 * @returns {JSX.Element|null} A canvas element displaying the signature, or `null` if the data is invalid.
 */
const RenderSignatureFromPaths = ({ signaturePaths }) => {
    const canvasRef = useRef();

    useEffect(() => {
        if (!signaturePaths || !canvasRef.current) {
            return;
        }

        const ctx = canvasRef.current.getContext("2d");

        let parsedPaths;
        try {
            parsedPaths = JSON.parse(signaturePaths);
        } catch (error) {
            console.error("Error parsing signature paths:", error);
            return;
        }

        if (!Array.isArray(parsedPaths) || !Array.isArray(parsedPaths[0])) {
            console.error("Invalid signature paths format. Expected an array of strokes.");
            return;
        }

        const canvasWidth = canvasRef.current.width;
        const canvasHeight = canvasRef.current.height;

        const allPoints = parsedPaths.flat();
        const minX = Math.min(...allPoints.map(p => p.x));
        const maxX = Math.max(...allPoints.map(p => p.x));
        const minY = Math.min(...allPoints.map(p => p.y));
        const maxY = Math.max(...allPoints.map(p => p.y));

        const signatureWidth = maxX - minX || 1;
        const signatureHeight = maxY - minY || 1;

        const scale = Math.min(
            canvasWidth / signatureWidth,
            canvasHeight / signatureHeight
        );

        const offsetX = (canvasWidth - (signatureWidth * scale)) / 2 - (minX * scale);
        const offsetY = (canvasHeight - (signatureHeight * scale)) / 2 - (minY * scale);

        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
        ctx.save();
        ctx.setTransform(scale, 0, 0, scale, offsetX, offsetY);

        parsedPaths.forEach((stroke) => {
            ctx.beginPath();
            stroke.forEach((point, index) => {
                if (index === 0) {
                    ctx.moveTo(point.x, point.y);
                } else {
                    ctx.lineTo(point.x, point.y);
                }
                ctx.strokeStyle = point.color || "black";
                ctx.lineWidth = 2 * scale * 0.8;
            });
            ctx.stroke();
        });

        ctx.restore();
    }, [signaturePaths]);

    if (!signaturePaths) {
        return null;
    }

    try {
        const parsedPaths = JSON.parse(signaturePaths);
        if (!Array.isArray(parsedPaths) || !Array.isArray(parsedPaths[0])) {
            return null;
        }
    } catch {
        return null;
    }

    return <canvas ref={canvasRef} width={300} height={150} style={{ border: "1px solid #ccc" }} />;
};

RenderSignatureFromPaths.propTypes = {
    signaturePaths: PropTypes.string.isRequired,
};

export default RenderSignatureFromPaths;
